// Auto-advancing scroll-snap row for small screens with prev/next and dot commands, looping back to the start.
// Commands (buttons with commandfor=<this id>): --prev, --next, --go (source carries data-index).
// Dots are [data-autoplay-dot] buttons; the active one gets data-active and aria-current="true".
class SAutoplayRow extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.track = this.querySelector('[data-autoplay-track]');
    if (!this.track) return;

    this.interval = Number(this.getAttribute('interval')) || 3000;
    this.mobileQuery = window.matchMedia(this.getAttribute('media') || '(max-width: 767px)');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.dots = Array.from(this.querySelectorAll('[data-autoplay-dot]'));
    this.pausedUntil = 0;

    const { signal } = this.controller;
    const pause = () => {
      this.pausedUntil = Date.now() + this.interval * 2;
    };
    this.track.addEventListener('pointerdown', pause, { signal });
    this.track.addEventListener('touchstart', pause, { signal, passive: true });
    this.track.addEventListener('focusin', pause, { signal });

    let scrollFrame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => this.updateDots());
      },
      { signal, passive: true },
    );

    this.addEventListener(
      'command',
      (event) => {
        pause();
        const items = this.items();
        const current = this.currentIndex();
        if (event.command === '--next') this.goTo((current + 1) % items.length);
        if (event.command === '--prev') this.goTo((current - 1 + items.length) % items.length);
        if (event.command === '--go') this.goTo(Number(event.source?.dataset.index) || 0);
      },
      { signal },
    );

    this.timer = window.setInterval(() => this.advance(), this.interval);
    this.updateDots();
  }

  disconnectedCallback() {
    this.controller?.abort();
    window.clearInterval(this.timer);
  }

  items() {
    return Array.from(this.track.children);
  }

  currentIndex() {
    const items = this.items();
    const trackRect = this.track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let closest = 0;
    let closestDistance = Infinity;
    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = index;
      }
    });
    return closest;
  }

  goTo(index) {
    const items = this.items();
    const target = items[index];
    if (!target) return;
    const behavior = this.motionQuery.matches ? 'auto' : 'smooth';
    this.track.scrollTo({ left: target.offsetLeft - items[0].offsetLeft, behavior });
  }

  updateDots() {
    if (this.dots.length === 0) return;
    const current = this.currentIndex();
    this.dots.forEach((dot, index) => {
      const active = index === current;
      dot.toggleAttribute('data-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  advance() {
    if (!this.mobileQuery.matches || this.motionQuery.matches) return;
    if (Date.now() < this.pausedUntil || document.hidden) return;
    const items = this.items();
    if (items.length < 2) return;
    this.goTo((this.currentIndex() + 1) % items.length);
  }
}

if (!customElements.get('s-autoplay-row')) {
  customElements.define('s-autoplay-row', SAutoplayRow);
}
